import _ from 'lodash';
import {get, post} from 'sdk';
import {BASE_URL} from 'appConstants';

import {transformFiltersForStoreToFiltersForApi} from 'utils/filters';
import {getFormattedDateForApiOrValue} from 'utils/datetime';
import {transformSortForStoreToSortForUrl} from 'utils/sort';

export const fetchBookingSets = ({locationRef, extraGetParams}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${locationRef}/`;

  return get(url, extraGetParams);
};

export const fetchTenantOptions = ({locationRef, search}) => {
  const url = `${BASE_URL}/v1/living/tenant/${locationRef}/primary/`;

  return get(url, {search});
};

export const fetchBookingSet = ({bookingSetId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/detail/`;

  return get(url);
};

export const fetchBookingSetsByTenant = ({locationRef, tenantId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${locationRef}/`;

  return get(url, {tenant: tenantId});
};

export const fetchBookingSetsFinanceData = ({bookingSetId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/finance/`;

  return get(url);
};

export const fetchBookingOptionsForBookingSet = ({
  search,
  bookingSetId,
  locationRef
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/booking-options/`;

  return get(url, {search, location: locationRef});
};

export const fetchVoidableInvoicesForBookingSet = ({bookingSetId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/voidable-invoices/`;

  return get(url);
};

export const fetchBookingSetDepositFinanceData = ({bookingSetId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/deposit-finance/`;

  return get(url);
};

export const fetchImageUrlAndFileName = ({imageIdentifier}) => {
  const url = `${BASE_URL}/v1/living/images/${imageIdentifier}/image-filename-and-url/`;

  return get(url);
};

export const fetchDocumentFileName = ({documentIdentifier}) => {
  const url = `${BASE_URL}/v1/living/documents/${documentIdentifier}/document-filename/`;

  return get(url);
};

export const fetchDepositSummaryStatuses = () => {
  const url = `${BASE_URL}/v1/living/deposit-summary-statuses/`;

  return get(url);
};

export const fetchRoomChecksTimeline = ({bookingSetId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/room-checks-timeline-statuses/`;

  return get(url);
};

export const exportBookingSetsCsvInBackground = ({
  filters,
  locationRef,
  sort
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${locationRef}/export-csv-in-background/`;

  const normalizedFilters = _.mapValues(filters, getFormattedDateForApiOrValue);

  const params = {
    ...transformFiltersForStoreToFiltersForApi(normalizedFilters),
    ...transformSortForStoreToSortForUrl(sort)
  };

  return get(url, params);
};

export const fetchArrivalRoomChecks = ({bookingSetId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/`;

  return get(url);
};

export const fetchDepartureRoomChecks = ({bookingSetId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/`;

  return get(url);
};

export const fetchRoomOptionsForAddArrivalRoomCheck = ({bookingSetId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/room-options/`;

  return get(url);
};

export const fetchRoomOptionsForAddDepartureRoomCheck = ({bookingSetId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/room-options/`;

  return get(url);
};

export const signRoomCheckImage = ({bookingSetId, filename, fileType}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/image-sign/`;
  const payload = {filename, fileType, bookingSetId};

  return post(url, payload);
};

export const createArrivalRoomCheck = ({
  bookingSetId,
  booking,
  existingDamages,
  description,
  imageIdentifiers,
  inventoryIdentifier
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/create/`;
  const payload = {
    booking,
    existingDamages,
    description,
    imageIdentifiers,
    inventoryIdentifier
  };

  return post(url, payload);
};

export const createDepartureRoomCheck = ({
  bookingSetId,
  booking,
  totalDamageCharge,
  roomCheckDescription,
  imageIdentifiers,
  inventoryIdentifier,
  maintenanceIdentifier,
  paymentTakenAtFrontDesk,
  chargeFromDeposit,
  releaseFullDeposit,
  label,
  description,
  oneOffChargeReasonId,
  amount,
  createPayment,
  paymentMethod,
  paymentDate,
  invoiceDueDate,
  reference,
  xeroAccount,
  batchNumber,
  creditCardNumber
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/create/`;
  const payload = {
    booking,
    totalDamageCharge,
    roomCheckDescription,
    imageIdentifiers,
    inventoryIdentifier,
    maintenanceIdentifier,
    paymentTakenAtFrontDesk,
    chargeFromDeposit,
    releaseFullDeposit,
    label,
    description,
    oneOffChargeReasonId,
    amount,
    createPayment,
    paymentMethod,
    paymentDate,
    invoiceDueDate,
    reference,
    xeroAccount,
    batchNumber,
    creditCardNumber
  };

  return post(url, payload);
};

export const signInventoryDocument = ({bookingSetId, filename, fileType}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/inventory-sign/`;
  const payload = {filename, fileType};

  return post(url, payload);
};

export const fetchArrivalRoomCheck = ({bookingSetId, roomCheckId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/${roomCheckId}/`;

  return get(url);
};

export const fetchDepartureRoomCheck = ({bookingSetId, roomCheckId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${roomCheckId}/`;

  return get(url);
};

export const fetchDepositTypes = () => {
  const url = `${BASE_URL}/v1/living/deposit-types/`;

  return get(url);
};

export const verifyArrivalRoomCheckImageUpload = ({
  bookingSetId,
  imageIdentifier,
  arrivalRoomCheckId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/${arrivalRoomCheckId}/image-verify-upload/${imageIdentifier}/`;

  return post(url);
};

export const removeArrivalRoomCheckImage = ({
  bookingSetId,
  arrivalRoomCheckId,
  roomCheckImageId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/${arrivalRoomCheckId}/remove-image/${roomCheckImageId}/`;

  return post(url);
};

export const verifyArrivalRoomCheckInventoryUpload = ({
  bookingSetId,
  documentIdentifier,
  arrivalRoomCheckId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/${arrivalRoomCheckId}/inventory-verify-upload/${documentIdentifier}/`;

  return post(url);
};

export const removeArrivalRoomCheckInventory = ({
  bookingSetId,
  arrivalRoomCheckId,
  roomCheckInventoryId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/${arrivalRoomCheckId}/remove-inventory/${roomCheckInventoryId}/`;

  return post(url);
};

export const editArrivalRoomCheckExistingDamages = ({
  bookingSetId,
  arrivalRoomCheckId,
  newExistingDamages
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/${arrivalRoomCheckId}/edit-existing-damages/`;
  const payload = {newExistingDamages};

  return post(url, payload);
};

export const editArrivalRoomCheckDescription = ({
  bookingSetId,
  arrivalRoomCheckId,
  newDescription
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/${arrivalRoomCheckId}/edit-description/`;
  const payload = {newDescription};

  return post(url, payload);
};

export const editDepositSummaryAmountToBeRetainedPaid = ({
  bookingSetId,
  newAmountToBeRetainedPaid
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/deposit-summary/edit-amount-to-be-retained-paid/`;
  const payload = {newAmountToBeRetainedPaid};

  return post(url, payload);
};

export const editDepositSummaryRefundToBeIssuedPaid = ({
  bookingSetId,
  newRefundToBeIssuedPaid
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/deposit-summary/edit-refund-to-be-issued-paid/`;
  const payload = {newRefundToBeIssuedPaid};

  return post(url, payload);
};

export const editDepositReleaseDueDate = ({
  bookingSetId,
  newDepositReleaseDueDate
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/deposit-summary/edit-deposit-release-due-date/`;
  const payload = {newDepositReleaseDueDate};

  return post(url, payload);
};

export const signDepartureRoomCheckImage = ({
  bookingSetId,
  filename,
  fileType
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/image-sign/`;
  const payload = {filename, fileType, bookingSetId};

  return post(url, payload);
};

export const editDepartureRoomCheckDescription = ({
  bookingSetId,
  departureRoomCheckId,
  newDescription
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/edit-description/`;
  const payload = {newDescription};

  return post(url, payload);
};

export const editDepartureRoomCheckTotalDamageCharge = ({
  bookingSetId,
  departureRoomCheckId,
  newTotalDamageCharge
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/edit-total-damage-charge/`;
  const payload = {newTotalDamageCharge};

  return post(url, payload);
};

export const editDepartureRoomCheckChargeFromDeposit = ({
  bookingSetId,
  departureRoomCheckId,
  newChargeFromDeposit
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/edit-charge-from-deposit/`;
  const payload = {newChargeFromDeposit};

  return post(url, payload);
};

export const editDepartureRoomCheckReleaseFullDeposit = ({
  bookingSetId,
  departureRoomCheckId,
  newReleaseFullDeposit
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/edit-release-full-deposit/`;
  const payload = {newReleaseFullDeposit};

  return post(url, payload);
};

export const editDepartureRoomCheckStatus = ({
  bookingSetId,
  departureRoomCheckId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/edit-status/`;

  return post(url);
};

export const editDepositSummaryDepositType = ({
  bookingSetId,
  newDepositType
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/deposit-summary/edit-deposit-type/`;
  const payload = {newDepositType};

  return post(url, payload);
};

export const signDepartureRoomCheckInventoryDocument = ({
  bookingSetId,
  filename,
  fileType
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/inventory-sign/`;
  const payload = {filename, fileType};

  return post(url, payload);
};

export const signDepartureRoomCheckMaintenanceDocument = ({
  bookingSetId,
  filename,
  fileType
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/maintenance-sign/`;
  const payload = {
    filename,
    fileType
  };

  return post(url, payload);
};

export const verifyDepartureRoomCheckImageUpload = ({
  bookingSetId,
  imageIdentifier,
  departureRoomCheckId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/image-verify-upload/${imageIdentifier}/`;

  return post(url);
};

export const removeDepartureRoomCheckImage = ({
  bookingSetId,
  departureRoomCheckId,
  roomCheckImageId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/remove-image/${roomCheckImageId}/`;

  return post(url);
};

export const fetchDepositSummary = ({bookingSetId}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/deposit-summary/`;

  return get(url);
};

export const verifyDepartureRoomCheckInventoryUpload = ({
  bookingSetId,
  documentIdentifier,
  departureRoomCheckId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/inventory-document-verify-upload/${documentIdentifier}/`;

  return post(url);
};

export const removeDepartureRoomCheckInventory = ({
  bookingSetId,
  departureRoomCheckId,
  roomCheckInventoryId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/remove-inventory/${roomCheckInventoryId}/`;

  return post(url);
};

export const verifyDepartureRoomCheckMaintenanceUpload = ({
  bookingSetId,
  documentIdentifier,
  departureRoomCheckId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/maintenance-document-verify-upload/${documentIdentifier}/`;

  return post(url);
};

export const removeDepartureRoomCheckMaintenance = ({
  bookingSetId,
  departureRoomCheckId,
  roomCheckMaintenanceId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/departure-room-checks/${departureRoomCheckId}/remove-maintenance/${roomCheckMaintenanceId}/`;

  return post(url);
};

export const editArrivalRoomCheckStatus = ({
  bookingSetId,
  arrivalRoomCheckId
}) => {
  const url = `${BASE_URL}/v1/living/booking-sets/${bookingSetId}/arrival-room-checks/${arrivalRoomCheckId}/edit-status/`;

  return post(url);
};
